import simpleDDP from 'simpleddp';
import localforage from 'localforage';

import Chat from './chat';
import StickyButton from './chat/stickyButton';
import MessageSound from './utils/msgSound';

const KEY_SUBSCRIPTION_CREATED_AT_DATE = 'cleverpush-subscription-created-at-date';
const CONTENT_TYPE = {
  IMAGE: 'image',
  VIDEO: 'video',
  AUDIO: 'audio'
};
const WELCOME_MESSAGE = 'welcomeMessage';
const MEDIA_CONTENT_TYPES = [CONTENT_TYPE.IMAGE, CONTENT_TYPE.VIDEO, CONTENT_TYPE.AUDIO];

function debounce(func, wait, immediate) {
  let timeout;
  return function () {
    const context = this;
    const args = arguments;
    const later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

const filterMessages = (messages, createdAtDate) => {
  if (!createdAtDate || !messages) {
    return messages;
  }

  return messages.filter((message) => !message.createdAt || message.createdAt.getTime() >= createdAtDate);
};

export default class CleverpushChat {
  constructor(subscription, config, isOpenInWidget) {
    this.messages = [];

    this.config = config;
    this.subscriptionId = subscription;
    this.preview = this.subscriptionId === 'preview';
    this.status = 'connecting';
    this.saveMessages = this.saveMessages.bind(this);
    this.saveMessagesDebounced = debounce(this.saveMessages, 1000);
    this.msgsLoaded = false;
    this.isOpenInWidget = isOpenInWidget || false;
    this.createGuestSubscription = this.createGuestSubscription.bind(this);
    this.originalTitle = JSON.parse(sessionStorage.getItem('cleverpush-client-document-title'));
    this.isGuestSubscriptionUpdated = false;

    this.msgSound = new MessageSound();

    // This line will inject 'cleverpush-chat-target' to the html file
    if (!this.isOpenInWidget) {
      const chatTarget = document.createElement('div');
      chatTarget.className = 'cleverpush-chat-target';
      document.body.appendChild(chatTarget);
    }

    localforage.config({
      name: 'cleverpush-chat',
      version: 1.0,
      storeName: 'cleverpush-chat-local',
      description: 'IndexedDB storage for local synchronized chat messages',
    });

    this.loadMessages()
      .then(() => {
        console.log('CleverPush chat: localForage init done');
        this.connect();
      })
      .catch((err) => {
        console.log('CleverPush chat: localForage init failed', err);
        this.connect();
      });
  }

  getCreatedAtDate = async (fallbackDate = true) => {
    let createdAtDate = Number(localStorage.getItem(KEY_SUBSCRIPTION_CREATED_AT_DATE));
    if (!createdAtDate || isNaN(createdAtDate)) {
      try {
        const createdAtFromServer = await this.server.call('chat.getSubscriptionCreatedAt', this.config.channelId, this.subscriptionId);
        console.log('createdAtFromServer', createdAtFromServer);
        createdAtDate = new Date(createdAtFromServer).getTime();
      } catch (error) {
        console.log('CleverPush chat: failed to get subscription created at date', error);
      }
      if (!createdAtDate && fallbackDate) {
        createdAtDate = Date.now();
      }
      if (createdAtDate) {
        localStorage.setItem(KEY_SUBSCRIPTION_CREATED_AT_DATE, `${createdAtDate}`);
      }
    }
    console.log('getCreatedAtDate', createdAtDate, 'fallbackDate', fallbackDate ? 'true' : 'false');
    return createdAtDate;
  };

  // Function to get the welcome messages
  getWelcomeMessages = () => {
    const welcomeMessageSteps = this.config.chatWelcomeMessage?.steps[0];
    const welcomeMessages = [];
    if (welcomeMessageSteps?.messages.length) {
      welcomeMessageSteps.messages.forEach((message) => {
        const randomId = Array.from(crypto.getRandomValues(new Uint8Array(9))).map(String).join('').substr(0, 17);
        const welcomeMessage = {
          id: randomId,
          fromChannel: true,
          channel: this.config.channelId,
          subscription: this.subscriptionId,
          name: WELCOME_MESSAGE,
          createdAt: new Date(),
          read: false,
          type: message.type,
          messageType: WELCOME_MESSAGE
        };
        if (MEDIA_CONTENT_TYPES.includes(message.type)) {
          welcomeMessage.mediaUrl = message.mediaUrl || '';
        } else {
          welcomeMessage.text = message.title || '';
        }
        welcomeMessages.push(welcomeMessage);
      });
      return welcomeMessages;
    }
    return welcomeMessages;
  }

  // Function to add welcome messages
  addWelcomeMessages = (messages, welcomeMessages) => {
    if (!this.preview) {
      sessionStorage.setItem('cleverpush-client-messages', JSON.stringify(welcomeMessages));
      return [...messages, ...welcomeMessages];
    }
    return messages;
  };

  // Logic for adding or removing a welcome message if newMsgsFromChannel is different from welcomeMessage.
  updateWelcomeMessage = (msgs, welcomeMsgs) => {
    const welcomeMsgCount = welcomeMsgs.length;
    const welcomeMsgCountFromMsgs = msgs.reduce((acc, current) => {
      if (Object.keys(current).includes('messageType')) {
        if (current.messageType === WELCOME_MESSAGE) {
          acc += 1;
        }
      }
      return acc;
    }, 0);
    const difference = welcomeMsgCount - welcomeMsgCountFromMsgs;
    if (difference > 0) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < Math.abs(difference); i++) {
        msgs.unshift(welcomeMsgs[i]);
      }
    } else if (difference < 0) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < Math.abs(difference); i++) {
        msgs.shift();
      }
    }
    welcomeMsgs.forEach((welcomeMsg, index) => {
      if (msgs[index].messageType === welcomeMsg.messageType) {
        msgs[index].type = welcomeMsg.type;
        if (MEDIA_CONTENT_TYPES.includes(msgs[index].type)) {
          if (msgs[index].mediaUrl !== welcomeMsg.mediaUrl) {
            msgs[index].mediaUrl = welcomeMsg.mediaUrl || '';
            delete msgs[index].text;
          }
        } else if (msgs[index].text !== welcomeMsg.text) {
          msgs[index].text = welcomeMsg.text || '';
          delete msgs[index].mediaUrl;
        }
      }
      if (msgs[index].name !== welcomeMsg.name) {
        msgs[index].name = welcomeMsg.name;
      }
    });
    const welcomeMessageIndex = this.messages.findIndex((message) => this.filterMessage(message, this.subscriptionId));
    this.messages.splice(welcomeMessageIndex, welcomeMsgCountFromMsgs);
    msgs.forEach((msg, index) => {
      this.messages.splice(welcomeMessageIndex + index, 0, msg);
    });
    const channelMsgs = this.messages.filter((doc) => doc.fromChannel);
    sessionStorage.setItem('cleverpush-client-messages', JSON.stringify(channelMsgs));
  };

  filterMessage = (message, subscriptionId) => (
    message.subscription === subscriptionId
    && message.fromChannel
    && message.messageType === WELCOME_MESSAGE
  )

  getFromChannelMessages = (messages, subscriptionId) => {
    const fromChannelMessages = messages.filter(
      (message) => this.filterMessage(message, subscriptionId)
    ).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    return fromChannelMessages;
  }

  async addWelcomeMessageForGuest() {
    const conversation = await this.server.call('chat.getConversation', this.subscriptionId);
    const welcomeMessages = this.getWelcomeMessages();
    if (conversation) {
      const firstMessage = this.messages
        .filter((message) => message.subscription === this.subscriptionId && message.fromChannel === false)
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))[0];

      if (firstMessage) {
        const getNextIndex = this.messages.indexOf(firstMessage) + 1;
        const newDate = new Date(firstMessage.createdAt.getTime() + 1);
        const updatedWelcomeMessages = welcomeMessages.map((welcomeMessage) => ({
          ...welcomeMessage,
          createdAt: newDate,
        }));
        const fromChannelMessages = this.getFromChannelMessages(this.messages, this.subscriptionId);
        if (!fromChannelMessages.length) {
          updatedWelcomeMessages.forEach((welcomeMessage, index) => {
            this.messages.splice(getNextIndex + index, 0, welcomeMessage);
          });
          sessionStorage.setItem('cleverpush-client-messages', JSON.stringify(welcomeMessages));
        } else {
          this.updateWelcomeMessage(fromChannelMessages, welcomeMessages);
        }
      }
    }
  }

  loadMessages() {
    return (async () => {
      // localforage indexeddb fix
      if (/iP(hone|od|ad)/.test(navigator.platform)) {
        const v = navigator.appVersion.match(/OS (\d+)_(\d+)_?(\d+)?/);
        if (parseInt(v[1], 10) >= 11) {
          const driver = await localforage.getDriver(localforage.INDEXEDDB);
          const custom = Object.assign(true, driver, {
            _driver: 'customIndexedDB',
            _support: true,
          });
          localforage.defineDriver(custom);
        }
      }
      let messages = await localforage.getItem('cleverpush-chat-messages') || [];

      if (this.config.chatOnlyNewMessagesEnabled) {
        const createdAtDate = await this.getCreatedAtDate(false);
        messages = filterMessages(messages, createdAtDate);
      }

      if (messages && messages.length > 0 && !this.msgsLoaded) {
        this.msgsLoaded = true;
        this.messages = messages;
        const msgsFromChannel = messages.filter((msg) => msg.fromChannel);
        sessionStorage.setItem('cleverpush-client-messages', JSON.stringify(msgsFromChannel));
      }
    })();
  }

  connect() {
    let endpoint = 'wss://liveapi.cleverpush.com/websocket';
    if (process.env.NODE_ENV === 'development') {
      endpoint = 'ws://localhost:3010/websocket';
    } else if (process.env.NODE_ENV === 'stage') {
      endpoint = 'wss://liveapi-stage.cleverpush.com/websocket';
    }

    const opts = {
      endpoint,
      SocketConstructor: WebSocket,
      reconnectInterval: 3000,
      maxTimeout: 10000,
      autoConnect: true,
      autoReconnect: true,
      clearDataOnReconnection: false,
    };

    this.server = new simpleDDP(opts);

    this.chat = new Chat(
      this.subscriptionId,
      this.config,
      this.messages,
      this.server,
      this.isOpenInWidget,
      this.createGuestSubscription,
    );

    this.renderChat();

    this.server.on('connected', () => {
      this.status = 'connected';

      this.subscribePublications();

      this.renderChat();

      const messages = this.server
        .collection('messages')
        .reactive({ sort: { createdAt: -1 } });
      const toDelete = this.server
        .collection('deletedMessages')
        .reactive({ limit: 25 });
      messages.onChange(async (msgs) => {
        let messagesChanged = false;

        const createdAtDate = this.config.chatOnlyNewMessagesEnabled ? await this.getCreatedAtDate() : null;
        msgs.forEach((msg) => {
          let push = true;
          const msgLocal = this.messages.find((m) => m.id === msg.id);
          if (msgLocal) {
            if (msgLocal.text !== msg.text) {
              msgLocal.text = msg.text;
              messagesChanged = true;
            }
            push = false;
          }

          if (push) {
            if (createdAtDate && msg.createdAt && msg.createdAt.getTime() < createdAtDate) {
              return;
            }

            messagesChanged = true;
            this.messages.push(msg);
          }
        });

        if (messagesChanged) {
          this.msgsLoaded = true;
          this.messages.sort((a, b) => a.createdAt - b.createdAt);

          this.renderChat();

          this.saveMessages(msgs);
        }
      });
      toDelete.onChange((dels) => {
        let deletedChanged = false;

        dels.forEach((del) => {
          for (let i = 0; i < this.messages.length; i += 1) {
            if (this.messages[i] && this.messages[i].id === del.id) {
              // only delete private messages
              if (this.messages[i].subscription) {
                this.server.call('chat.removeDeleteId', del.id);
              }
              this.messages.splice(i, 1);

              deletedChanged = true;
            }
          }
        });

        if (deletedChanged) {
          localforage
            .setItem('cleverpush-chat-messages', this.messages)
            .then(() => {
              this.renderChat();
            })
            .catch((err) => {
              console.log(err);
              this.renderChat();
            });
        }
      });
    });

    this.server.on('disconnected', () => {
      this.status = 'disconnected';
      this.renderChat();
    });

    this.server.on('error', (e) => {
      this.status = 'disconnected';
      this.renderChat();
      console.log('Error while cleverpush chats widget connection ..', e);
    });
  }

  async subscribePublications() {
    try {
      const channelId = this.config.channelId || '';
      const broadcasts = this.server.subscribe(
        (this.config.chatOnlyNewMessagesEnabled || this.preview) ? 'chat.broadcasts' : 'chat.broadcasts.preview',
        channelId,
        this.subscriptionId
      );

      let privates;
      let deleted;
      if (!this.preview) {
        privates = this.server.subscribe(
          'chat.privates',
          channelId,
          this.subscriptionId
        );
        deleted = this.server.subscribe(
          'chat.deletedMessages',
          channelId
        );
      }

      await broadcasts.ready();
      if (!this.preview) {
        await privates.ready();
        await deleted.ready();
      }
    } catch (err) {
      console.error(err);
    }
  }

  createGuestSubscription = async (guestName, guestEmail) => {
    const bodyData = {
      topics: [],
      emailAddress: guestEmail
    };

    if (guestName) {
      bodyData.name = guestName;
    }

    const oldGuestSubscriptionID = localStorage.getItem('cleverpush-guest-subscription-id') ? localStorage.getItem('cleverpush-guest-subscription-id') : '';

    const guestSubscriptionID = await this.server.call('guest.subscriptions.insert', this.config.channelId, bodyData, oldGuestSubscriptionID);

    localStorage.setItem('cleverpush-guest-subscription-id', guestSubscriptionID);

    this.subscriptionId = guestSubscriptionID;

    this.preview = false;

    this.subscribePublications();

    return guestSubscriptionID;
  }

  setDocumentTitle = (msgCount) => {
    if (msgCount !== 0) {
      document.title = `${this.originalTitle} (${msgCount})`;
    } else {
      document.title = JSON.parse(sessionStorage.getItem('cleverpush-client-document-title'));
    }
  }

  saveMessages(msgs) {
    try {
      console.log(`CleverPush chat: localforage set 0 ${this.messages.length}`);

      if (this.messages.length > 100) {
        this.messages = this.messages.slice(this.messages.length - 100);
      }

      let subscriptionId = '';
      try {
        subscriptionId = localStorage.getItem('cleverpush-subscription-id');
      } catch (ignored) { }
      const isGuest = this.config.chatGuestsEnabled && !subscriptionId;

      if (!isGuest && !this.isGuestSubscriptionUpdated) {
        this.messages = this.messages.map((message) => ({ ...message, subscription: this.subscriptionId }));
        this.isGuestSubscriptionUpdated = true;
      }

      localforage.setItem('cleverpush-chat-messages', this.messages, (err) => {
        console.log('CleverPush chat: localforage set 1');
        if (err) {
          console.log(err);
        } else {
          const directMsgs = msgs.filter((msg) => msg.subscription && !msg.read);
          const directMsgIds = directMsgs.map((doc) => doc.id);
          const sessionStorageMessagesFromChannel = JSON.parse(sessionStorage.getItem('cleverpush-client-messages'));
          const channelMsgs = this.messages.filter((doc) => doc.fromChannel);

          try {
            if (directMsgs[directMsgs.length - 1]?.fromChannel && sessionStorageMessagesFromChannel[sessionStorageMessagesFromChannel.length - 1] !== channelMsgs[channelMsgs.length - 1]) {
              // Find new channel messages
              let currentMessageCount = JSON.parse(sessionStorage.getItem('cleverpush-client-message-count'));
              currentMessageCount = Number(currentMessageCount) + Number(1);
              this.setDocumentTitle(currentMessageCount);
              // this.msgSound.playBell();
              sessionStorage.setItem('cleverpush-client-message-count', JSON.stringify(currentMessageCount));
            }
          } catch (error) {
            console.info('Cleverpush :- caught error while ringing bell', error);
          }

          // This will add all received messages from channel
          sessionStorage.setItem('cleverpush-client-messages', JSON.stringify(channelMsgs));

          if (directMsgIds.length) {
            this.server.call('chat.messages.markAsRead', directMsgIds);
          }
        }
      });
    } catch (e) {
      console.log(e.message);
    }
  }

  getFirstMessage() {
    const sorted = (this.messages || []).sort(
      (a, b) => a.createdAt - b.createdAt
    );
    if (sorted.length) {
      return sorted[0];
    }
    return null;
  }

  async renderChat() {
    window.addEventListener('focus', (e) => {
      document.title = JSON.parse(sessionStorage.getItem('cleverpush-client-document-title'));
      sessionStorage.setItem('cleverpush-client-message-count', JSON.stringify(0));
    });
    console.log('CleverPush chat: render chat');
    try {
      this.renderingChat = true;
      if (this.config.chatShowWelcomeMessage) {
        //  welcome message for guest subscription user.
        if (this.config.chatGuestsEnabled) {
          await this.addWelcomeMessageForGuest();
        } else {
          // welcome message for subscription user.
          const welcomeMessages = this.getWelcomeMessages();
          const fromChannelMessages = this.getFromChannelMessages(this.messages, this.subscriptionId);
          if (!fromChannelMessages.length) {
            this.messages = this.addWelcomeMessages(this.messages, welcomeMessages);
          } else {
            this.updateWelcomeMessage(fromChannelMessages, welcomeMessages);
          }
        }
      }
      this.chat.renderMessages(this.messages, this.status, this.server);
      this.renderingChat = false;
    } catch (e) {
      console.log(e);
    }
  }
}

window.cleverpushChatLoaded = true;

if (window.cleverpushConfig && window.cleverpushSubscriptionId) {
  new CleverpushChat(window.cleverpushSubscriptionId, window.cleverpushConfig);
} else {
  const initChat = () => {
    sessionStorage.setItem('cleverpush-client-document-title', JSON.stringify(document.title));
    sessionStorage.setItem('cleverpush-client-messages', JSON.stringify([]));
    sessionStorage.setItem('cleverpush-client-message-count', JSON.stringify(0));

    console.log('Initialized - src/cleverpush-chat.js', window.CleverPush.config);

    let chatTopic;
    if (window.CleverPush.config.channelTopics) {
      chatTopic = window.CleverPush.config.channelTopics.find(
        (t) => t.isChatTopic
      );
    }
    if (chatTopic) {
      window.CleverPush.config.chatTopicId = chatTopic._id;
    }

    window.CleverPush.isSubscribed((subscribed) => {
      const guestSubscriptionId = localStorage.getItem('cleverpush-guest-subscription-id') ? localStorage.getItem('cleverpush-guest-subscription-id') : '';

      if (window.CleverPush.config.displayChatWidget) {
        const stickyButton = new StickyButton(window.CleverPush.config);
        stickyButton.render();
      } else if (subscribed) {
        window.CleverPush.waitForSubscription()
          .then((subscriptionId) => {
            if (chatTopic) {
              window.CleverPush.getTopics((err, topics) => {
                if (topics && topics.indexOf(chatTopic._id) > -1) {
                  new CleverpushChat(subscriptionId, window.CleverPush.config);
                } else {
                  new CleverpushChat('preview', window.CleverPush.config);
                }
              });
            } else {
              new CleverpushChat(subscriptionId, window.CleverPush.config);
            }
          })
          .catch((ignored) => { });
      } else if (window.CleverPush.config.chatGuestsEnabled && guestSubscriptionId !== '') {
        new CleverpushChat(guestSubscriptionId, window.CleverPush.config);
      } else {
        new CleverpushChat('preview', window.CleverPush.config);
      }
    });
  };

  if (!window.CleverPush || !window.CleverPush.initialized) {
    window.cleverPushInitCallback = () => {
      initChat();
    };
  } else {
    initChat();
  }
}
